import { createContext, useContext } from 'react';

/**
 * "Is a priority notice open?" — the one rule that keeps auto-opening dialogs
 * from stacking.
 *
 * A priority notice (today: the privacy policy update notice,
 * components/legal/PrivacyPolicyNotice, and after it the terms of service
 * re-acceptance, components/legal/TermsOfServiceConsent) takes precedence over every other
 * dialog that opens by itself (onboarding tour, news/release-notes popup, …).
 * While `active` is true such a dialog must not open (or must stay hidden) and
 * may open once it turns false; badges/dots simply stay. `active` is already
 * true while the notice is still being checked after sign-in, so another
 * dialog does not open first and get covered a moment later.
 *
 * Usage in an auto-opening dialog:
 *   const priorityNoticeActive = usePriorityNoticeActive();
 *   <Dialog isOpen={wantsOpen && !priorityNoticeActive} … />
 *
 * Without a provider (isolated page/component tests) it is always false.
 */
export interface PriorityNoticeState {
  /** A priority notice is open, or is being checked and may open. */
  active: boolean;
}

export const PriorityNoticeContext = createContext<PriorityNoticeState>({ active: false });

export function usePriorityNoticeActive(): boolean {
  return useContext(PriorityNoticeContext).active;
}
